import { supabase, TABLES } from '../config/supabase';
import logger from '../utils/logger';

class AdvancedEmailAutomationService {
  constructor() {
    this.isProcessing = false;
    this.settings = null;
    // N8N webhook URL for advanced email automation (from environment)
    this.webhookUrl = process.env.REACT_APP_N8N_EMAIL_AUTOMATION_WEBHOOK_URL || '';
    this.webhookEnabled = true; // Enable webhook calls for production
    this.processingInterval = null;
    this.processingLock = false; // Add explicit lock for race condition prevention
    this.lastProcessingTime = null;
  }

  /**
   * Initialize the advanced service
   */
  async initialize() {
    try {
      await this.loadSettings();
      
      // Check if server-side automation is active
      const serverSideActive = await this.checkServerSideAutomation();
      
      if (serverSideActive) {
        logger.log('🔧 Server-side automation is active - client-side processing will be minimal');
        this.serverSideMode = true;
        // Still start processor but with reduced frequency for monitoring
        this.startSequenceProcessor(true); // Pass true for server-side mode
      } else {
        logger.log('🔧 Server-side automation not detected - using client-side processing');
        this.serverSideMode = false;
        this.startSequenceProcessor();
      }
      
      logger.log('✅ Advanced Email Automation Service initialized successfully');
      logger.log('🔄 Follow-up sequence processor is running');
    } catch (error) {
      logger.error('❌ Failed to initialize Advanced Email Automation Service:', error);
    }
  }

  /**
   * Check if server-side automation is active
   */
  async checkServerSideAutomation() {
    try {
      // Try to call the server-side status function
      const { data, error } = await supabase.rpc('get_server_automation_status');
      
      if (error) {
        logger.log('Server-side automation functions not found, using client-side mode');
        return false;
      }
      
      if (data) {
        // jsonb return: data is a single object; TABLE return: data is an array
        const status = Array.isArray(data) ? data[0] : data;
        if (status) {
          logger.log('🔍 Server-side automation status:', status);
          return status.cron_job_active && !status.is_paused;
        }
      }
      
      return false;
    } catch (error) {
      logger.log('Server-side automation check failed, using client-side mode');
      return false;
    }
  }

  /**
   * Load automation settings with advanced features
   */
  async loadSettings() {
    try {
      const { data, error } = await supabase
        .from('email_automation_settings')
        .select('*')
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (!data) {
        // Create default settings with advanced features
        const { data: newSettings, error: createError } = await supabase
          .from('email_automation_settings')
          .insert({
            is_enabled: true,
            max_daily_sends: 100,
            current_daily_sends: 0,
            monitoring_enabled: true,
            webhook_url: this.webhookUrl,
            default_delay_days: 7,
            campaign_monitoring_enabled: true,
            reply_check_enabled: true,
            time_window_enabled: true,
            duplicate_protection_enabled: true,
            sequence_management_enabled: true,
            followup_call_enabled: true,
            default_time_window: {
              start_time: '01:00',
              end_time: '09:00',
              timezone: 'Europe/Helsinki',
              randomize_within_window: true,
              min_delay_minutes: 1,
              max_delay_minutes: 10
            }
          })
          .select()
          .single();

        if (createError) throw createError;
        this.settings = newSettings;
      } else {
        this.settings = data;
      }

      logger.log('⚙️ Advanced settings loaded:', {
        enabled: this.settings.is_enabled,
        sequences: this.settings.sequence_management_enabled,
        duplicates: this.settings.duplicate_protection_enabled,
        time_windows: this.settings.time_window_enabled,
        followup_calls: this.settings.followup_call_enabled
      });
    } catch (error) {
      logger.error('Error loading advanced settings:', error);
    }
  }

  /**
   * Advanced open event processing with reply checking and inbox extraction
   */
  async processOpenEventAdvanced(leadEvent) {
    try {
      if (!this.settings?.is_enabled) {
        logger.log('⏸️ Advanced email automation is disabled');
        return;
      }

      // Check if sequence management is enabled in settings
      if (this.settings?.sequence_management_enabled === false) {
        logger.log('⏸️ Sequence management is disabled in settings');
        return;
      }

      if (leadEvent.event_type !== 'open') {
        logger.log('⏭️ Skipping non-open event:', leadEvent.event_type);
        return;
      }

      if (!leadEvent.campaign_id) {
        logger.warn('⚠️ Skipping open event - no campaign_id:', leadEvent.lead_email);
        return;
      }

      logger.log('🔍 Processing advanced open event:', {
        email: leadEvent.lead_email,
        campaign: leadEvent.campaign_name,
        campaign_id: leadEvent.campaign_id
      });

      // Use the advanced database function to process the event
      const { data: result, error } = await supabase
        .rpc('process_open_event_advanced', {
          p_lead_email: leadEvent.lead_email,
          p_campaign_id: leadEvent.campaign_id,
          p_campaign_name: leadEvent.campaign_name,
          p_raw_data: leadEvent.raw_data || {}
        });

      if (error) {
        logger.error('Error processing advanced open event:', error);
        return;
      }

      logger.log('📊 Advanced processing result:', result);

      // Handle the result based on action
      switch (result.action) {
        case 'stop':
          // NOTE: Reply detection no longer prevents sequence creation.
          // All leads must receive follow-up emails even if they have replied.
          // Only leads MANUALLY stopped via Inbox will stop receiving emails.
          logger.log('ℹ️ Reply detected but sequences will continue (manual stop only)');
          break;
        
        case 'sequence_created':
          logger.log('✅ Sequence created successfully');
          logger.log('📋 Sequence ID:', result.sequence_id);
          logger.log('⏰ Next scheduled:', result.next_scheduled_at);
          logger.log('🤖 Database orchestrator will create execution at scheduled time');
          // DO NOT call scheduleSequenceStep - let the database handle it!
          break;
        
        case 'already_in_sequence':
          logger.log('ℹ️ Lead already in sequence - Step:', result.current_step);
          break;
        
        case 'add_to_call_list':
          logger.log('📞 Added to follow-up call list - Campaign not active');
          break;
        
        case 'max_sequences_reached':
          logger.log('⚠️ Max sequences limit reached for this lead');
          break;
        
        default:
          logger.log('ℹ️ Action:', result.action || 'none');
          break;
      }

      return result;
    } catch (error) {
      logger.error('Error in advanced open event processing:', error);
    }
  }

  /**
   * Schedule a sequence step with time window and duplicate protection
   */
  async scheduleSequenceStep(sequenceId, step) {
    try {
      // Get sequence information
      const { data: sequence, error: seqError } = await supabase
        .from('email_automation_sequences')
        .select(`
          *,
          email_automation_campaign_activation (
            template_id,
            sequence_config,
            sending_time_window
          )
        `)
        .eq('id', sequenceId)
        .single();

      if (seqError || !sequence) {
        logger.error('Sequence not found:', sequenceId);
        return;
      }

      // Get template for this step using the new function
      const { data: templateId, error: templateError } = await supabase
        .rpc('get_template_for_sequence_step', {
          p_campaign_id: sequence.campaign_id,
          p_sequence_step: step
        });

      if (templateError || !templateId) {
        logger.error('No valid template found for step:', step, templateError);
        return;
      }

      // Validate template before proceeding
      const { data: templateValid, error: validationError } = await supabase
        .from('email_automation_templates')
        .select('id, subject_template, content_template, is_active')
        .eq('id', templateId)
        .eq('is_active', true)
        .single();

      if (validationError || !templateValid) {
        logger.error('Template validation failed:', templateId, validationError);
        return;
      }

      if (!templateValid.subject_template || !templateValid.content_template ||
          templateValid.subject_template.trim() === '' || templateValid.content_template.trim() === '') {
        logger.error('Template has empty subject or content:', templateId);
        return;
      }

      // Check for duplicates
      const isDuplicate = await this.checkDuplicate(
        sequence.lead_email,
        sequence.campaign_id,
        templateId,
        step
      );

      if (isDuplicate) {
        logger.log('🚫 Duplicate email detected, skipping');
        return;
      }

      // Get delay for this step
      const stepConfig = sequence.email_automation_campaign_activation.sequence_config.steps[step - 1];
      const delayDays = stepConfig?.delay_days || 7;

      // Get lead data for template processing
      const { data: leadData, error: leadError } = await supabase
        .from(TABLES.LEADS)
        .select('*')
        .eq('email', sequence.lead_email)
        .single();

      let lead;
      if (leadError || !leadData) {
        logger.error('Lead not found for template processing:', sequence.lead_email);
        // Continue with basic lead data
        lead = {
          email: sequence.lead_email,
          first_name: 'Valued Customer',
          last_name: '',
          company: 'Your Company',
          campaign_name: sequence.campaign_name || 'Our Campaign'
        };
      } else {
        lead = leadData;
      }

      // Process template content immediately when creating execution
      const emailContent = this.processTemplateContent(templateValid.content_template, lead, {
        campaign_id: sequence.campaign_id,
        campaign_name: sequence.campaign_name
      });
      
      const emailSubject = this.processTemplateContent(
        templateValid.custom_subject || templateValid.subject_template,
        lead,
        { campaign_id: sequence.campaign_id, campaign_name: sequence.campaign_name }
      );

      // Create execution record with processed template content
      const { data: execution, error: execError } = await supabase
        .from('email_automation_executions')
        .insert({
          template_id: templateId,
          lead_email: sequence.lead_email,
          sequence_id: sequenceId,
          sequence_step: step,
          original_inbox_email: sequence.original_inbox_email,
          email_subject: emailSubject,
          email_content: emailContent,
          delay_minutes: delayDays * 24 * 60, // Convert days to minutes
          status: 'pending',
          scheduled_at: new Date(Date.now() + delayDays * 24 * 60 * 60 * 1000)
        })
        .select()
        .single();

      if (execError) {
        logger.error('Error creating execution:', execError);
        return;
      }

      // Schedule with time window
      const scheduledTime = await this.scheduleWithTimeWindow(
        execution.id,
        sequenceId,
        sequence.campaign_id,
        sequence.lead_email,
        delayDays,
        sequence.email_automation_campaign_activation.sending_time_window
      );

      // Update execution with scheduled time
      await supabase
        .from('email_automation_executions')
        .update({ scheduled_send_time: scheduledTime })
        .eq('id', execution.id);

      // Update sequence's next_scheduled_at to match the actual scheduled time
      // This ensures the UI displays the correct time after time window adjustments
      await supabase
        .from('email_automation_sequences')
        .update({ 
          next_scheduled_at: scheduledTime,
          updated_at: new Date().toISOString()
        })
        .eq('id', sequenceId);

      logger.log(`✅ Scheduled sequence step ${step} for ${sequence.lead_email} at ${scheduledTime} with template ${templateId}`);
      logger.log(`📧 Template processed - Subject: "${emailSubject}", Content length: ${emailContent.length} chars`);

      return execution;
    } catch (error) {
      logger.error('Error scheduling sequence step:', error);
    }
  }

  /**
   * Check for duplicate emails
   */
  async checkDuplicate(leadEmail, campaignId, templateId, step) {
    try {
      if (!this.settings?.duplicate_protection_enabled) {
        return false;
      }

      const { data: isDuplicate, error } = await supabase
        .rpc('check_duplicate_email', {
          p_lead_email: leadEmail,
          p_campaign_id: campaignId,
          p_template_id: templateId,
          p_sequence_step: step
        });

      if (error) {
        logger.error('Error checking duplicate:', error);
        return false;
      }

      return isDuplicate;
    } catch (error) {
      logger.error('Error in duplicate check:', error);
      return false;
    }
  }

  /**
   * Schedule email with time window management
   */
  async scheduleWithTimeWindow(executionId, sequenceId, campaignId, leadEmail, delayDays, timeWindow) {
    try {
      // Check if time window management is enabled in settings
      if (this.settings?.time_window_enabled === false) {
        logger.log('⏰ Time window management disabled in settings, using simple scheduling');
        return new Date(Date.now() + delayDays * 24 * 60 * 60 * 1000);
      }

      // If no time window is provided or doesn't have required properties, use simple scheduling
      if (!timeWindow || !timeWindow.start_time || !timeWindow.end_time) {
        logger.log('⏰ No time window configured, using simple scheduling');
        return new Date(Date.now() + delayDays * 24 * 60 * 60 * 1000);
      }

      logger.log('⏰ Using time window scheduling:', JSON.stringify(timeWindow, null, 2));

      const { data: scheduledTime, error } = await supabase
        .rpc('schedule_email_with_time_window', {
          p_execution_id: executionId,
          p_sequence_id: sequenceId,
          p_campaign_id: campaignId,
          p_lead_email: leadEmail,
          p_delay_days: delayDays,
          p_time_window: timeWindow
        });

      if (error) {
        logger.error('Error scheduling with time window:', error);
        return new Date(Date.now() + delayDays * 24 * 60 * 60 * 1000);
      }

      logger.log('⏰ Scheduled time from database function:', scheduledTime);
      return scheduledTime;
    } catch (error) {
      logger.error('Error in time window scheduling:', error);
      return new Date(Date.now() + delayDays * 24 * 60 * 60 * 1000);
    }
  }

  /**
   * Process pending sequences and executions
   */
  async processSequences() {
    // If server-side mode is active, skip client-side processing
    if (this.serverSideMode) {
      logger.log('⏭️ Server-side automation active, skipping client-side processing');
      return;
    }

    // Prevent concurrent processing
    if (this.processingLock) {
      logger.log('⏭️ Sequence processing already in progress, skipping');
      return;
    }

    // Prevent too frequent processing
    const now = Date.now();
    if (this.lastProcessingTime && (now - this.lastProcessingTime) < 30000) {
      logger.log('⏭️ Processing too frequent, waiting...');
      return;
    }

    try {
      this.processingLock = true;
      this.lastProcessingTime = now;
      this.isProcessing = true;
      logger.log('🔄 Processing pending sequences...');

      // Get pending executions that are ready to send
      const { data: pendingExecutions, error } = await supabase
        .from('email_automation_executions')
        .select(`
          *,
          email_automation_templates (*),
          email_automation_sequences (*)
        `)
        .eq('status', 'pending')
        .lte('scheduled_send_time', new Date().toISOString())
        .order('scheduled_send_time', { ascending: true })
        .limit(100);

      if (error) {
        logger.error('Error fetching pending executions:', error);
        return;
      }

      if (!pendingExecutions || pendingExecutions.length === 0) {
        logger.log('📭 No pending executions to process');
        return;
      }

      logger.log(`📨 Processing ${pendingExecutions.length} pending executions`);

      for (const execution of pendingExecutions) {
        await this.executeSequenceStep(execution);
        // Add small delay between executions to prevent overwhelming
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

    } catch (error) {
      logger.error('Error processing sequences:', error);
    } finally {
      this.isProcessing = false;
      this.processingLock = false;
    }
  }

  /**
   * Execute a sequence step
   */
  async executeSequenceStep(execution) {
    try {
      logger.log(`🚀 Executing sequence step ${execution.sequence_step} for ${execution.lead_email}`);

      // Validate execution before sending
      const { data: isValid, error: validationError } = await supabase
        .rpc('validate_execution_before_send', {
          p_execution_id: execution.id
        });

      if (validationError || !isValid) {
        logger.error('Execution validation failed:', execution.id, validationError);
        await supabase
          .from('email_automation_executions')
          .update({
            status: 'failed',
            error_message: validationError?.message || 'Execution validation failed',
            executed_at: new Date().toISOString()
          })
          .eq('id', execution.id);
        return;
      }

      // Get sequence data to access campaign_id
      const { data: sequence, error: sequenceError } = await supabase
        .from('email_automation_sequences')
        .select('campaign_id')
        .eq('id', execution.sequence_id)
        .single();

      if (sequenceError || !sequence) {
        logger.error('Error fetching sequence data:', sequenceError);
        return;
      }

      // NOTE: Reply detection no longer stops sequences automatically.
      // All follow-up sequences must send all steps at their configured times.
      // Only leads that are MANUALLY stopped via Inbox will stop receiving emails.
      // The checkIfLeadReplied() check has been intentionally removed here.

      // Get lead data
      const { data: lead, error: leadError } = await supabase
        .from(TABLES.LEADS)
        .select('*')
        .eq('email', execution.lead_email)
        .single();

      if (leadError || !lead) {
        logger.error('Lead not found for execution:', execution.lead_email);
        return;
      }

      // Send via webhook with original inbox routing
      await this.sendSequenceEmailViaWebhook(execution, lead, sequence.campaign_id);

      // Record duplicate protection
      await this.recordDuplicate(execution, sequence.campaign_id);

      // Schedule next step if sequence should continue
      // Use dynamic max_steps from sequence data instead of hardcoded 5
      const maxSteps = execution.email_automation_sequences?.max_steps || 5;
      if (execution.email_automation_sequences && execution.sequence_step < maxSteps) {
        await this.scheduleNextSequenceStep(execution.sequence_id);
      }

    } catch (error) {
      logger.error('Error executing sequence step:', error);
      
      // Mark execution as failed
      await supabase
        .from('email_automation_executions')
        .update({
          status: 'failed',
          error_message: error.message,
          executed_at: new Date().toISOString()
        })
        .eq('id', execution.id);
    }
  }

  /**
   * Determine inbox route based on original inbox email
   */
  determineInboxRoute(originalInbox) {
    if (!originalInbox) return 'inbox1';
    
    const inbox = originalInbox.toLowerCase();
    
    if (inbox.includes('jani')) {
      return 'inbox1'; // jani@otherwiselight.org
    } else if (inbox.includes('sara')) {
      return 'inbox2'; // sara@otherwiselight.org
    } else if (inbox.includes('neimiceo')) {
      return 'inbox3'; // neimiceo@otherwiselight.org
    }
    
    // Default to inbox1 (jani)
    return 'inbox1';
  }

  /**
   * Send sequence email via webhook with original inbox routing
   */
  async sendSequenceEmailViaWebhook(execution, lead, campaignId) {
    const maxRetries = 3;
    const timeoutMs = 30000; // 30 seconds
    
    try {
      if (!this.webhookEnabled) {
        logger.log('⚠️ Webhook disabled - skipping sequence email send (development mode)');
        return { ok: true, status: 200, message: 'Webhook disabled' };
      }
      
      // Validate required fields
      if (!execution?.id || !lead?.email) {
        throw new Error('Execution ID and lead email are required for webhook payload');
      }
      
      if (!execution.original_inbox_email) {
        throw new Error('Original inbox email is required for sequence automation');
      }
      
      const template = execution.email_automation_templates;
      
      // Use pre-processed template content from execution record
      let emailContent = execution.email_content;
      let emailSubject = execution.email_subject;
      
      // Fallback to processing if not available (backward compatibility)
      if (!emailContent || !emailSubject) {
        logger.log('⚠️ Template content not pre-processed, processing now...');
        
        if (!template?.content_template) {
          throw new Error('Template content is required but not available');
        }
        
        emailContent = this.processTemplateContent(template.content_template, lead, {
          campaign_id: campaignId,
          campaign_name: lead.campaign_name
        });
        
        emailSubject = this.processTemplateContent(
          template.custom_subject || template.subject_template,
          lead,
          { campaign_id: campaignId }
        );
      }

      // Mint the message_id in the CRM so it exists BEFORE the send and is not
      // dependent on n8n's post-send PATCH landing. Same format n8n uses
      // (angle-bracketed, @otherwiselight.org) so downstream handling is unchanged.
      const messageId = `<${Date.now()}-${Math.random().toString(36).substring(2, 15)}@otherwiselight.org>`;

      // Prepare webhook payload with original inbox routing
      const webhookPayload = {
        action: 'send_automation_email',
        execution_id: execution.id,
        sequence_step: execution.sequence_step || 1,
        message_id: messageId, // n8n adopts this for the pixel + Message-ID header
        timestamp: new Date().toISOString(),
        lead_data: {
          email: lead.email,
          first_name: lead?.first_name || '',
          last_name: lead?.last_name || null,
          company: lead?.company || '',
          campaign_id: campaignId,
          campaign_name: lead?.campaign_name || 'Unknown Campaign',
          workspace_id: lead?.workspace_id || null
        },
        email_data: {
          recipient_email: lead.email,
          sender_email: execution.original_inbox_email, // Use original inbox
          subject: emailSubject,
          html_body: emailContent,
          automation_type: `sequence_step_${execution.sequence_step || 1}`,
          original_inbox: execution.original_inbox_email
        },
        automation_data: {
          template_id: template?.id || null,
          scheduled_at: execution.scheduled_send_time || execution.scheduled_at || new Date().toISOString(),
          executed_at: new Date().toISOString(),
          sequence_id: execution.sequence_id || null,
          sequence_step: execution.sequence_step || 1
        }
      };

      logger.log('📤 Sending sequence email via webhook:', {
        lead: lead.email,
        step: execution.sequence_step,
        inbox: execution.original_inbox_email,
        subject: emailSubject,
        contentLength: emailContent.length
      });

      // Send webhook with retry logic
      const sendWebhookWithRetry = async (attempt = 0) => {
        try {
          // Create AbortController for timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
          
          const startTime = Date.now();
          const response = await fetch(this.webhookUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(webhookPayload),
            signal: controller.signal
          });

          clearTimeout(timeoutId);
          const responseTime = Date.now() - startTime;
          const responseText = await response.text();

          // Log webhook call
          await this.logWebhookCall(
            execution.id,
            this.webhookUrl,
            webhookPayload,
            response.status,
            responseText,
            responseTime,
            response.ok ? null : `HTTP ${response.status}`
          );

          if (!response.ok) {
            throw new Error(`Webhook failed: ${response.status} ${responseText}`);
          }

          return { responseText, responseTime };
        } catch (error) {
          logger.error(`Webhook attempt ${attempt + 1} failed:`, error);
          
          // Check if this is a retryable error
          const isRetryable = error.name === 'AbortError' || 
                             error.code === 'ECONNRESET' || 
                             error.code === 'ENOTFOUND' || 
                             error.code === 'ETIMEDOUT' ||
                             (error.status && error.status >= 500);
          
          if (isRetryable && attempt < maxRetries) {
            const delay = Math.pow(2, attempt) * 1000; // Exponential backoff
            logger.log(`⏳ Retrying webhook call in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            return sendWebhookWithRetry(attempt + 1);
          }
          
          throw error;
        }
      };

      await sendWebhookWithRetry();

      // Mark execution as sent via DB function (also increments total_sent counter)
      const { data: sendResult, error: sendError } = await supabase
        .rpc('mark_execution_sent', {
          p_execution_id: execution.id,
          p_message_id: messageId
        });

      if (sendError) {
        logger.error('Error marking execution sent via RPC:', sendError);
        // Fallback: direct update without counter increment
        await supabase
          .from('email_automation_executions')
          .update({
            status: 'sent',
            executed_at: new Date().toISOString(),
            actual_send_time: new Date().toISOString(),
            error_message: null
          })
          .eq('id', execution.id);
      } else {
        logger.log(`📊 Execution marked sent via DB:`, sendResult);
      }

      // Record a 'sent' lead_event so CRM/n8n-pipeline sends show up on the Campaigns
      // card and dashboards. Instantly campaigns get this row from the Instantly webhook
      // (instantlyWebhookHandler -> lead_events, event_type='sent'); the CRM send path
      // previously only updated email_automation_executions via mark_execution_sent, which
      // does NOT create a lead_events row, so these sends never incremented the card's
      // "Sent" count. This mirrors the one-time backfill in
      // FIX-RESEND-A-ATTRIBUTION-AND-PACING.sql (STEP 5) for all future sends.
      // Non-fatal + idempotent (dedup on execution_id) so it can never break sending.
      try {
        await this.recordSentLeadEvent(execution, lead, campaignId, messageId);
      } catch (evtErr) {
        logger.warn('⚠️ Failed to record sent lead_event (non-fatal):', evtErr?.message || evtErr);
      }

      logger.log(`✅ Sequence email sent successfully for ${lead.email} (step ${execution.sequence_step})`);

    } catch (error) {
      logger.error('Error sending sequence email via webhook:', error);
      
      // Update execution status to failed
      await supabase
        .from('email_automation_executions')
        .update({
          status: 'failed',
          executed_at: new Date().toISOString(),
          error_message: error.message
        })
        .eq('id', execution.id);
      
      throw error;
    }
  }

  /**
   * Record a 'sent' lead_event for a successful CRM/n8n automation send.
   *
   * Why this exists: the Campaigns card (get_campaign_stats_realtime + the client
   * overlay in DataContext.getCampaignStatsRealtime) derives every metric from the
   * lead_events table grouped by campaign_name. Instantly-sent campaigns get their
   * 'sent' rows from the Instantly webhook, so their cards update live. The CRM send
   * path only updated email_automation_executions (via mark_execution_sent) and never
   * inserted a lead_events row, so CRM-native campaigns (the Otherwise resend test)
   * never incremented their "Sent" count after the one-time SQL backfill.
   *
   * Idempotent: skips if a 'sent' event already exists for this execution_id, so a
   * webhook retry or re-run can never double-count. Uses the same row shape as the
   * Instantly handler and the SQL backfill (score_delta=0, step, campaign attribution).
   */
  async recordSentLeadEvent(execution, lead, campaignId, messageId) {
    const executionId = execution?.id;
    if (!executionId) return;

    // Dedup guard: don't insert twice for the same execution.
    const { data: existing, error: existingError } = await supabase
      .from('lead_events')
      .select('id')
      .eq('event_type', 'sent')
      .contains('metadata', { execution_id: executionId })
      .limit(1);

    if (existingError) {
      logger.warn('⚠️ Could not check for existing sent event, skipping to be safe:', existingError.message);
      return;
    }
    if (existing && existing.length > 0) {
      return; // already recorded
    }

    const { error: insertError } = await supabase
      .from('lead_events')
      .insert({
        lead_id: lead?.id || null,
        lead_email: execution.lead_email || lead?.email,
        event_type: 'sent',
        score_delta: 0,
        campaign_name: lead?.campaign_name || 'Unknown Campaign',
        campaign_id: campaignId,
        workspace_id: lead?.workspace_id || null,
        step: execution.sequence_step,
        metadata: {
          source: 'crm_automation_send',
          execution_id: executionId,
          sequence_id: execution.sequence_id || null,
          message_id: messageId || null
        },
        processed_at: new Date().toISOString()
      });

    if (insertError) throw insertError;

    logger.log(`📝 Recorded 'sent' lead_event for ${execution.lead_email} (execution ${executionId})`);
  }

  /**
   * Check if lead has replied
   */
  async checkIfLeadReplied(leadEmail, campaignId) {
    try {
      const { data: replyEvents, error } = await supabase
        .from('lead_events')
        .select('id, created_at')
        .eq('lead_email', leadEmail)
        .eq('campaign_id', campaignId)
        .eq('event_type', 'reply')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        logger.error('Error checking for replies:', error);
        return false;
      }

      return replyEvents && replyEvents.length > 0;
    } catch (error) {
      logger.error('Error checking if lead replied:', error);
      return false;
    }
  }

  /**
   * Stop sequence when lead replies
   */
  async stopSequenceOnReply(leadEmail, campaignId) {
    try {
      const { error } = await supabase
        .rpc('stop_sequence_on_reply', {
          p_lead_email: leadEmail,
          p_campaign_id: campaignId
        });

      if (error) {
        logger.error('Error stopping sequence on reply:', error);
        return;
      }

      logger.log(`🛑 Stopped sequence for ${leadEmail} due to reply`);

      // Add follow-up tag to lead
      await this.addFollowUpTag(leadEmail);
      
      // NOTE: Score is NOT added here anymore
      // Scoring is now handled by the AI validation system
      // Score will be added ONLY after AI confirms reply is valid (not auto-reply/OOO)
      // See: replyValidationService.js and update_reply_validation_status() function

    } catch (error) {
      logger.error('Error in stop sequence on reply:', error);
    }
  }

  /**
   * Schedule next sequence step
   */
  async scheduleNextSequenceStep(sequenceId) {
    try {
      const { data: result, error } = await supabase
        .rpc('process_next_sequence_step', {
          p_sequence_id: sequenceId
        });

      if (error) {
        logger.error('Error scheduling next sequence step:', error);
        return;
      }

      if (result.action === 'schedule_next_step') {
        logger.log(`📅 Scheduled next sequence step: ${result.next_step}`);
        await this.scheduleSequenceStep(sequenceId, result.next_step);
      } else if (result.action === 'sequence_completed') {
        logger.log('✅ Sequence completed');
      }

    } catch (error) {
      logger.error('Error in schedule next sequence step:', error);
    }
  }

  /**
   * Record duplicate protection
   */
  async recordDuplicate(execution, campaignId) {
    try {
      if (!this.settings?.duplicate_protection_enabled) {
        return;
      }

      const fingerprint = this.createDuplicateFingerprint(
        execution.lead_email,
        campaignId,
        execution.template_id,
        execution.sequence_step
      );

      await supabase
        .from('email_automation_duplicates')
        .insert({
          lead_email: execution.lead_email,
          campaign_id: campaignId,
          template_id: execution.template_id,
          sequence_step: execution.sequence_step,
          sent_at: new Date().toISOString(),
          execution_id: execution.id,
          fingerprint: fingerprint
        });

    } catch (error) {
      logger.error('Error recording duplicate:', error);
    }
  }

  /**
   * Create duplicate fingerprint
   */
  createDuplicateFingerprint(leadEmail, campaignId, templateId, step) {
    // Simple hash function for browser compatibility
    const data = `${leadEmail}|${campaignId}|${templateId}|${step}`;
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }

  /**
   * Add follow-up tag to lead
   */
  async addFollowUpTag(leadEmail) {
    try {
      const { data: lead, error: fetchError } = await supabase
        .from(TABLES.LEADS)
        .select('tags')
        .eq('email', leadEmail)
        .single();

      if (fetchError) {
        logger.error('Error fetching lead for tagging:', fetchError);
        return;
      }

      const currentTags = lead.tags || [];
      if (!currentTags.includes('follow up')) {
        const newTags = [...currentTags, 'follow up'];
        
        await supabase
          .from(TABLES.LEADS)
          .update({ tags: newTags })
          .eq('email', leadEmail);

        logger.log(`🏷️ Added 'follow up' tag to ${leadEmail}`);
      }
    } catch (error) {
      logger.error('Error adding follow-up tag:', error);
    }
  }

  /**
   * Update lead score
   */
  async updateLeadScore(leadEmail, scoreIncrease) {
    try {
      // First get current score, then update (supabase.raw() doesn't exist in JS client)
      const { data: lead, error: fetchError } = await supabase
        .from(TABLES.LEADS)
        .select('score')
        .eq('email', leadEmail)
        .single();

      if (fetchError || !lead) {
        logger.error('Error fetching lead score:', fetchError);
        return;
      }

      const newScore = (lead.score || 0) + scoreIncrease;
      const { error } = await supabase
        .from(TABLES.LEADS)
        .update({ 
          score: newScore,
          updated_at: new Date().toISOString()
        })
        .eq('email', leadEmail);

      if (error) {
        logger.error('Error updating lead score:', error);
        return;
      }

      logger.log(`📊 Updated score for ${leadEmail} (+${scoreIncrease})`);
    } catch (error) {
      logger.error('Error in update lead score:', error);
    }
  }

  processTemplateContent(template, lead, triggerEvent) {
    if (!template) return '';
    
    let content = template;
    
    // Ensure lead object exists
    const safeLeadData = lead || {};
    
    // Replace lead variables with proper fallbacks
    content = content.replace(/{{first_name}}/g, safeLeadData.first_name || 'there');
    content = content.replace(/{{last_name}}/g, safeLeadData.last_name || '');
    content = content.replace(/{{email}}/g, safeLeadData.email || '');
    content = content.replace(/{{company}}/g, safeLeadData.company || 'your company');
    content = content.replace(/{{campaign_name}}/g, safeLeadData.campaign_name || triggerEvent?.campaign_name || 'our campaign');
    content = content.replace(/{{source}}/g, safeLeadData.source || 'Email Marketing');
    
    // Handle any remaining unreplaced variables by removing them or providing generic fallbacks
    content = content.replace(/{{[^}]+}}/g, (match) => {
      logger.warn(`Unhandled template variable found: ${match}`);
      return ''; // Remove unhandled variables
    });
    
    return content;
  }

  /**
   * Log webhook call
   */
  async logWebhookCall(executionId, webhookUrl, requestPayload, responseStatus, responseBody, responseTimeMs, errorMessage) {
    try {
      await supabase
        .from('email_automation_webhook_logs')
        .insert({
          execution_id: executionId,
          webhook_url: webhookUrl,
          request_payload: requestPayload,
          response_status: responseStatus,
          response_body: responseBody,
          response_time_ms: responseTimeMs,
          error_message: errorMessage,
          created_at: new Date().toISOString()
        });
    } catch (error) {
      logger.error('Error logging webhook call:', error);
    }
  }

  /**
   * Start sequence processor with optional server-side mode
   */
  startSequenceProcessor(serverSideMode = false) {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
    }

    if (serverSideMode) {
      // In server-side mode, run less frequently for monitoring only
      this.processingInterval = setInterval(() => {
        this.monitorServerSideProcessing();
      }, 10 * 60 * 1000); // Every 10 minutes for monitoring
      
      logger.log('🔄 Sequence processor started in server-side monitoring mode (10-minute intervals)');
    } else {
      // Normal client-side processing every 2 minutes
      this.processingInterval = setInterval(() => {
        this.processSequences();
      }, 2 * 60 * 1000);
      
      logger.log('🔄 Sequence processor started in client-side mode (2-minute intervals)');
    }
  }

  /**
   * Monitor server-side processing (lightweight check)
   */
  async monitorServerSideProcessing() {
    try {
      logger.log('🔍 Monitoring server-side automation...');
      
      // Check server-side status
      const serverActive = await this.checkServerSideAutomation();
      
      if (!serverActive) {
        logger.log('⚠️ Server-side automation appears inactive, switching to client-side mode');
        this.serverSideMode = false;
        this.startSequenceProcessor(false);
        return;
      }
      
      // Optionally refresh lead email counts for UI updates
      await this.getLeadEmailCounts();
      
      logger.log('✅ Server-side automation is running normally');
    } catch (error) {
      logger.error('Error monitoring server-side processing:', error);
    }
  }

  /**
   * Stop sequence processor
   */
  stopSequenceProcessor() {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
      logger.log('⏹️ Sequence processor stopped');
    }
  }

  /**
   * Get follow-up call list
   */
  async getFollowUpCallList(campaignId = null) {
    try {
      // Check if follow-up call lists are enabled in settings
      if (this.settings?.followup_call_enabled === false) {
        logger.log('📞 Follow-up call lists disabled in settings');
        return [];
      }

      let query = supabase
        .from('email_automation_followup_calls')
        .select('*')
        .eq('status', 'pending')
        .order('open_event_date', { ascending: false });

      if (campaignId) {
        query = query.eq('campaign_id', campaignId);
      }

      const { data: calls, error } = await query;

      if (error) {
        logger.error('Error fetching follow-up calls:', error);
        return [];
      }

      return calls || [];
    } catch (error) {
      logger.error('Error in get follow-up call list:', error);
      return [];
    }
  }

  /**
   * Get individual lead email counts based on sequence step
   * Returns map of lead_email -> emails_sent_count
   */
  async getLeadEmailCounts() {
    try {
      const { data: sequences, error } = await supabase
        .from('email_automation_sequences')
        .select('lead_email, current_step, status');

      if (error) {
        logger.error('Error fetching lead email counts:', error);
        return {};
      }

      // Create map of lead_email -> emails_sent
      const leadCounts = {};
      sequences.forEach(sequence => {
        const emailsSent = Math.max(0, (sequence.current_step || 1) - 1);
        leadCounts[sequence.lead_email] = emailsSent;
      });

      return leadCounts;
    } catch (error) {
      logger.error('Error in get lead email counts:', error);
      return {};
    }
  }

  /**
   * Activate campaign for follow-up
   */
  async activateCampaignFollowUp(campaignId, campaignName, templateId = null, sequenceConfig = null, sendingTimeWindow = null) {
    try {
      // First, create or update the campaign activation record with the time window
      const { error: activationError } = await supabase
        .from('email_automation_campaign_activation')
        .upsert({
          campaign_id: campaignId,
          campaign_name: campaignName,
          template_id: templateId,
          sequence_config: sequenceConfig,
          sending_time_window: sendingTimeWindow,
          is_active: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'campaign_id'
        })
        .select()
        .single();

      if (activationError) {
        logger.error('Error creating/updating campaign activation:', activationError);
        return false;
      }

      logger.log(`✅ Created/updated campaign activation for: ${campaignName}`);
      
      // Move leads from follow-up call list to sequences
      await this.moveCallListToSequences(campaignId);
      
      return true;
    } catch (error) {
      logger.error('Error in activate campaign follow-up:', error);
      return false;
    }
  }

  /**
   * Move leads from follow-up call list to sequences when campaign is activated
   */
  async moveCallListToSequences(campaignId) {
    try {
      // Get leads from follow-up call list for this campaign
      const { data: callList, error: fetchError } = await supabase
        .from('email_automation_followup_calls')
        .select('*')
        .eq('campaign_id', campaignId)
        .eq('status', 'pending');

      if (fetchError) {
        logger.error('Error fetching call list:', fetchError);
        return;
      }

      if (!callList || callList.length === 0) {
        logger.log('📭 No leads in follow-up call list for campaign:', campaignId);
        return;
      }

      logger.log(`🔄 Moving ${callList.length} leads from call list to sequences`);

      for (const call of callList) {
        // Process as if it's a new open event
        await this.processOpenEventAdvanced({
          lead_email: call.lead_email,
          campaign_id: call.campaign_id,
          campaign_name: call.campaign_name,
          event_type: 'open',
          raw_data: {
            email_account: call.original_inbox_email,
            sender_email: call.original_inbox_email
          }
        });

        // Mark call as converted to sequence
        await supabase
          .from('email_automation_followup_calls')
          .update({ 
            status: 'converted',
            notes: 'Moved to sequence when campaign was activated'
          })
          .eq('id', call.id);
      }

      logger.log(`✅ Successfully moved ${callList.length} leads to sequences`);
    } catch (error) {
      logger.error('Error moving call list to sequences:', error);
    }
  }

  /**
   * Get sequence analytics with sequence-based email counting
   * Logic: current_step - 1 = emails sent (step 1 = 0 emails, step 2 = 1 email, etc.)
   */
  async getSequenceAnalytics() {
    try {
      const { data: analytics, error } = await supabase
        .from('email_automation_sequences')
        .select(`
          status,
          current_step,
          total_sent,
          campaign_id,
          created_at,
          lead_email
        `);

      if (error) {
        logger.error('Error fetching sequence analytics:', error);
        return null;
      }
      
      // Get ACTUAL sent emails count from executions (not assumption)
      const { count: actualSentCount, error: sentError } = await supabase
        .from('email_automation_executions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'sent');
      
      if (sentError) {
        logger.error('Error fetching sent executions count:', sentError);
      }

      // Process analytics with ACTUAL execution-based counting
      const stats = {
        total_sequences: analytics.length,
        active_sequences: analytics.filter(s => s.status === 'active').length,
        stopped_sequences: analytics.filter(s => s.status === 'stopped').length,
        paused_sequences: analytics.filter(s => s.status === 'paused').length,
        completed_sequences: analytics.filter(s => s.status === 'completed').length,
        replied_sequences: analytics.filter(s => s.status === 'replied').length,
        // FIXED: Use ACTUAL sent count from executions (not step assumption)
        total_emails_sent: actualSentCount || 0,
        avg_steps_completed: analytics.length > 0 ? 
          analytics.reduce((sum, s) => sum + s.current_step, 0) / analytics.length : 0,
        campaigns: [...new Set(analytics.map(s => s.campaign_id))].length,
        // Additional analytics for individual lead tracking
        sequence_details: analytics.map(s => ({
          lead_email: s.lead_email,
          campaign_id: s.campaign_id,
          status: s.status,
          current_step: s.current_step,
          emails_sent: Math.max(0, (s.current_step || 1) - 1),
          total_sent_legacy: s.total_sent || 0 // Keep legacy for comparison
        }))
      };

      logger.log('📊 Sequence Analytics (Execution-based counting):', {
        total_sequences: stats.total_sequences,
        total_emails_sent: stats.total_emails_sent,
        active_sequences: stats.active_sequences,
        note: 'Using ACTUAL sent executions, not step assumptions'
      });

      return stats;
    } catch (error) {
      logger.error('Error in get sequence analytics:', error);
      return null;
    }
  }

  /**
   * Get stopped sequences for display with sequence-based email counts
   */
  async getStoppedSequences() {
    try {
      const { data, error } = await supabase
        .from('email_automation_sequences')
        .select(`
          *,
          email_automation_campaign_activation(campaign_name, sequence_config)
        `)
        .eq('status', 'stopped')
        .order('updated_at', { ascending: false })
        .limit(500);
      
      if (error) {
        logger.error('Error loading stopped sequences:', error);
        return [];
      }
      
      // Add sequence-based email count to each sequence
      const sequencesWithCounts = (data || []).map(sequence => ({
        ...sequence,
        emails_sent_calculated: Math.max(0, (sequence.current_step || 1) - 1)
      }));
      
      return sequencesWithCounts;
    } catch (error) {
      logger.error('Error in get stopped sequences:', error);
      return [];
    }
  }

  /**
   * Delete a stopped sequence
   */
  async deleteStoppedSequence(sequenceId) {
    try {
      // First delete related records from email_automation_schedule
      const { error: scheduleError } = await supabase
        .from('email_automation_schedule')
        .delete()
        .eq('sequence_id', sequenceId);
      
      if (scheduleError) {
        logger.error('Error deleting schedule records:', scheduleError);
        throw scheduleError;
      }
      
      // Then delete related records from email_automation_executions
      const { error: executionsError } = await supabase
        .from('email_automation_executions')
        .delete()
        .eq('sequence_id', sequenceId);
      
      if (executionsError) {
        logger.error('Error deleting execution records:', executionsError);
        throw executionsError;
      }
      
      // Finally delete the sequence itself
      const { error } = await supabase
        .from('email_automation_sequences')
        .delete()
        .eq('id', sequenceId)
        .eq('status', 'stopped'); // Safety check - only delete stopped sequences
      
      if (error) {
        logger.error('Error deleting stopped sequence:', error);
        throw error;
      }
      
      logger.log('🗑️ Successfully deleted stopped sequence:', sequenceId);
      return true;
    } catch (error) {
      logger.error('Error in delete stopped sequence:', error);
      throw error;
    }
  }

  /**
   * Manually stop all automation for a specific lead (called from Inbox)
   * Only this manual action should stop sequences - not automatic reply detection
   */
  async manuallyStopLeadAutomation(leadEmail) {
    try {
      logger.log(`🛑 Manually stopping all automation for ${leadEmail}`);

      // 1. Stop all active sequences for this lead
      const { data: stoppedSequences, error: seqError } = await supabase
        .from('email_automation_sequences')
        .update({
          status: 'stopped',
          updated_at: new Date().toISOString()
        })
        .eq('lead_email', leadEmail)
        .in('status', ['active', 'paused'])
        .select('id');

      if (seqError) {
        logger.error('Error stopping sequences:', seqError);
        throw seqError;
      }

      // 2. Cancel all pending executions for this lead
      const { data: cancelledExecs, error: execError } = await supabase
        .from('email_automation_executions')
        .update({
          status: 'cancelled',
          error_message: 'Manually stopped by user from Inbox',
          updated_at: new Date().toISOString()
        })
        .eq('lead_email', leadEmail)
        .eq('status', 'pending')
        .select('id');

      if (execError) {
        logger.error('Error cancelling executions:', execError);
        throw execError;
      }

      // 3. Cancel scheduled items
      const { error: schedError } = await supabase
        .from('email_automation_schedule')
        .update({
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('lead_email', leadEmail)
        .eq('status', 'scheduled');

      if (schedError) {
        logger.error('Error cancelling schedules:', schedError);
      }

      const stoppedCount = stoppedSequences?.length || 0;
      const cancelledCount = cancelledExecs?.length || 0;

      logger.log(`✅ Manually stopped automation for ${leadEmail}: ${stoppedCount} sequences stopped, ${cancelledCount} executions cancelled`);

      return { 
        success: true, 
        stoppedSequences: stoppedCount, 
        cancelledExecutions: cancelledCount 
      };
    } catch (error) {
      logger.error('Error manually stopping automation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get automation status for a specific lead (used by Inbox to show status)
   */
  async getLeadAutomationStatus(leadEmail) {
    try {
      const { data, error } = await supabase
        .from('email_automation_sequences')
        .select('id, status, current_step, max_steps, campaign_id, next_scheduled_at')
        .eq('lead_email', leadEmail)
        .in('status', ['active', 'paused']);

      if (error) {
        logger.error('Error checking lead automation status:', error);
        return { hasActiveAutomation: false, activeSequences: [] };
      }

      return {
        hasActiveAutomation: data && data.length > 0,
        activeSequences: data || []
      };
    } catch (error) {
      logger.error('Error in getLeadAutomationStatus:', error);
      return { hasActiveAutomation: false, activeSequences: [] };
    }
  }

  /**
   * Get automation overview statistics for dashboard
   */
  async getAutomationOverviewStats() {
    try {
      const sequenceAnalytics = await this.getSequenceAnalytics();
      if (!sequenceAnalytics) return null;

      return {
        total_sequences: sequenceAnalytics.total_sequences,
        active_sequences: sequenceAnalytics.active_sequences,
        total_emails_sent: sequenceAnalytics.total_emails_sent,
        completed_sequences: sequenceAnalytics.completed_sequences,
        replied_sequences: sequenceAnalytics.replied_sequences,
        avg_steps_completed: sequenceAnalytics.avg_steps_completed,
        campaigns: sequenceAnalytics.campaigns
      };
    } catch (error) {
      logger.error('Error getting automation overview stats:', error);
      return null;
    }
  }

  /**
   * Get overdue sequences (active sequences past their scheduled time)
   */
  async getOverdueSequences(campaignId = null) {
    try {
      const { data, error } = await supabase
        .rpc('get_overdue_sequences', {
          p_campaign_id: campaignId || null
        });

      if (error) {
        logger.error('Error fetching overdue sequences via RPC:', error);
        // Fallback to direct query if RPC not available
        return await this.getOverdueSequencesFallback(campaignId);
      }

      return data || [];
    } catch (error) {
      logger.error('Error in getOverdueSequences:', error);
      return await this.getOverdueSequencesFallback(campaignId);
    }
  }

  /**
   * Fallback method to get overdue sequences via direct query
   */
  async getOverdueSequencesFallback(campaignId = null) {
    try {
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();

      let query = supabase
        .from('email_automation_sequences')
        .select(`
          id,
          lead_email,
          campaign_id,
          current_step,
          max_steps,
          next_scheduled_at,
          total_sent,
          original_inbox_email,
          email_automation_campaign_activation!inner(campaign_name, is_active)
        `)
        .eq('status', 'active')
        .not('next_scheduled_at', 'is', null)
        .lt('next_scheduled_at', oneHourAgo)
        .order('next_scheduled_at', { ascending: true });

      if (campaignId) {
        query = query.eq('campaign_id', campaignId);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Error in overdue sequences fallback:', error);
        return [];
      }

      // Transform to match RPC output format
      return (data || [])
        .filter(s => s.email_automation_campaign_activation?.is_active)
        .map(s => ({
          sequence_id: s.id,
          lead_email: s.lead_email,
          campaign_id: s.campaign_id,
          campaign_name: s.email_automation_campaign_activation?.campaign_name || 'Unknown',
          current_step: s.current_step,
          max_steps: s.max_steps,
          next_scheduled_at: s.next_scheduled_at,
          overdue_hours: Math.round((Date.now() - new Date(s.next_scheduled_at).getTime()) / 3600000 * 10) / 10,
          total_sent: s.total_sent || 0,
          original_inbox_email: s.original_inbox_email
        }));
    } catch (error) {
      logger.error('Error in overdue sequences fallback:', error);
      return [];
    }
  }

  /**
   * Reschedule overdue sequences (set next_scheduled_at to NOW so orchestrator picks them up)
   */
  async rescheduleOverdueSequences(campaignId = null) {
    try {
      const { data, error } = await supabase
        .rpc('reschedule_overdue_sequences', {
          p_campaign_id: campaignId || null
        });

      if (error) {
        logger.error('Error rescheduling via RPC:', error);
        // Fallback to direct update
        return await this.rescheduleOverdueSequencesFallback(campaignId);
      }

      logger.log('✅ Rescheduled overdue sequences:', data);
      return data;
    } catch (error) {
      logger.error('Error in rescheduleOverdueSequences:', error);
      return await this.rescheduleOverdueSequencesFallback(campaignId);
    }
  }

  /**
   * Fallback method to reschedule overdue sequences via direct update
   */
  async rescheduleOverdueSequencesFallback(campaignId = null) {
    try {
      const overdueSequences = await this.getOverdueSequencesFallback(campaignId);

      if (overdueSequences.length === 0) {
        return { rescheduled: 0, campaign_id: campaignId || 'all' };
      }

      const sequenceIds = overdueSequences.map(s => s.sequence_id);

      const { error } = await supabase
        .from('email_automation_sequences')
        .update({
          next_scheduled_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .in('id', sequenceIds);

      if (error) {
        logger.error('Error in reschedule fallback update:', error);
        return { rescheduled: 0, error: error.message };
      }

      logger.log(`✅ Rescheduled ${sequenceIds.length} overdue sequences`);
      return { rescheduled: sequenceIds.length, campaign_id: campaignId || 'all' };
    } catch (error) {
      logger.error('Error in reschedule fallback:', error);
      return { rescheduled: 0, error: error.message };
    }
  }

  /**
   * Get actual sent email counts per sequence from execution table
   * Returns map of sequence_id -> actual_sent_count
   */
  async getActualSentCounts(sequenceIds = null) {
    try {
      let query = supabase
        .from('email_automation_executions')
        .select('sequence_id')
        .eq('status', 'sent');

      if (sequenceIds && sequenceIds.length > 0) {
        query = query.in('sequence_id', sequenceIds);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Error fetching actual sent counts:', error);
        return {};
      }

      // Count sent executions per sequence_id
      const counts = {};
      (data || []).forEach(exec => {
        counts[exec.sequence_id] = (counts[exec.sequence_id] || 0) + 1;
      });

      return counts;
    } catch (error) {
      logger.error('Error in getActualSentCounts:', error);
      return {};
    }
  }

  /**
   * Get overdue and auto-reply-stopped sequences for a campaign, grouped by step
   * Used by the campaign configuration modal to show per-step counts
   */
  async getCampaignSequencesByStep(campaignId) {
    try {
      // Get overdue active sequences for this campaign
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();

      const { data: overdueData, error: overdueError } = await supabase
        .from('email_automation_sequences')
        .select('id, lead_email, current_step, max_steps, next_scheduled_at, status, original_inbox_email')
        .eq('campaign_id', campaignId)
        .eq('status', 'active')
        .not('next_scheduled_at', 'is', null)
        .lt('next_scheduled_at', oneHourAgo);

      if (overdueError) {
        logger.error('Error fetching overdue sequences for campaign:', overdueError);
      }

      // Get auto-reply-stopped sequences (stopped or replied status)
      const { data: stoppedData, error: stoppedError } = await supabase
        .from('email_automation_sequences')
        .select('id, lead_email, current_step, max_steps, status, updated_at, original_inbox_email')
        .eq('campaign_id', campaignId)
        .in('status', ['stopped', 'replied']);

      if (stoppedError) {
        logger.error('Error fetching stopped sequences for campaign:', stoppedError);
      }

      // Group by step
      const stepData = {};
      for (let step = 1; step <= 5; step++) {
        stepData[step] = {
          overdue: [],
          stopped: []
        };
      }

      (overdueData || []).forEach(seq => {
        const step = seq.current_step || 1;
        if (!stepData[step]) stepData[step] = { overdue: [], stopped: [] };
        stepData[step].overdue.push(seq);
      });

      (stoppedData || []).forEach(seq => {
        const step = seq.current_step || 1;
        if (!stepData[step]) stepData[step] = { overdue: [], stopped: [] };
        stepData[step].stopped.push(seq);
      });

      return {
        stepData,
        totalOverdue: (overdueData || []).length,
        totalStopped: (stoppedData || []).length,
        overdueSequences: overdueData || [],
        stoppedSequences: stoppedData || []
      };
    } catch (error) {
      logger.error('Error in getCampaignSequencesByStep:', error);
      return { stepData: {}, totalOverdue: 0, totalStopped: 0, overdueSequences: [], stoppedSequences: [] };
    }
  }

  /**
   * Reschedule campaign sequences (overdue + auto-reply-stopped)
   * Follows all orchestration rules: delay, time windows, send limits
   * @param {string} campaignId - Campaign to reschedule
   * @param {string} type - 'overdue', 'stopped', or 'all'
   */
  async rescheduleCampaignSequences(campaignId, type = 'all') {
    try {
      // Get campaign activation config for delay/time window rules
      const { data: activation, error: activationError } = await supabase
        .from('email_automation_campaign_activation')
        .select('*')
        .eq('campaign_id', campaignId)
        .eq('is_active', true)
        .single();

      if (activationError || !activation) {
        logger.error('Campaign activation not found:', activationError);
        return { rescheduled: 0, error: 'Campaign activation not found or inactive' };
      }

      const timeWindow = activation.sending_time_window;
      let sequencesToReschedule = [];

      // Get overdue sequences
      if (type === 'overdue' || type === 'all') {
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
        const { data: overdueSeqs, error: oErr } = await supabase
          .from('email_automation_sequences')
          .select('*')
          .eq('campaign_id', campaignId)
          .eq('status', 'active')
          .not('next_scheduled_at', 'is', null)
          .lt('next_scheduled_at', oneHourAgo);

        if (!oErr && overdueSeqs) {
          sequencesToReschedule.push(...overdueSeqs.map(s => ({ ...s, reschedule_type: 'overdue' })));
        }
      }

      // Get auto-reply-stopped sequences
      if (type === 'stopped' || type === 'all') {
        const { data: stoppedSeqs, error: sErr } = await supabase
          .from('email_automation_sequences')
          .select('*')
          .eq('campaign_id', campaignId)
          .in('status', ['stopped', 'replied']);

        if (!sErr && stoppedSeqs) {
          sequencesToReschedule.push(...stoppedSeqs.map(s => ({ ...s, reschedule_type: 'stopped' })));
        }
      }

      if (sequencesToReschedule.length === 0) {
        return { rescheduled: 0, type, campaign_id: campaignId };
      }

      let rescheduledCount = 0;

      for (const seq of sequencesToReschedule) {
        try {
          // Use scheduleWithTimeWindow for proper time window scheduling
          let nextScheduledAt;
          if (timeWindow && timeWindow.start_time && timeWindow.end_time) {
            nextScheduledAt = await this.scheduleWithTimeWindow(
              null, seq.id, campaignId, seq.lead_email, 0, timeWindow
            );
          } else {
            // If no time window, schedule for now (immediate pickup by orchestrator)
            nextScheduledAt = new Date().toISOString();
          }

          // Reactivate the sequence with new schedule
          const { error: updateError } = await supabase
            .from('email_automation_sequences')
            .update({
              status: 'active',
              next_scheduled_at: nextScheduledAt,
              updated_at: new Date().toISOString()
            })
            .eq('id', seq.id);

          if (!updateError) {
            rescheduledCount++;
          } else {
            logger.error(`Error rescheduling sequence ${seq.id}:`, updateError);
          }
        } catch (seqError) {
          logger.error(`Error processing sequence ${seq.id}:`, seqError);
        }
      }

      logger.log(`✅ Rescheduled ${rescheduledCount}/${sequencesToReschedule.length} sequences for campaign ${campaignId} (type: ${type})`);
      return {
        rescheduled: rescheduledCount,
        total: sequencesToReschedule.length,
        type,
        campaign_id: campaignId
      };
    } catch (error) {
      logger.error('Error in rescheduleCampaignSequences:', error);
      return { rescheduled: 0, error: error.message };
    }
  }

  /**
   * Reschedule sequences for a specific step within a campaign.
   * Supports custom date/time and template override per step.
   * Only affects overdue or reply-stopped sequences at that step.
   * @param {string} campaignId
   * @param {number} step - The step number to reschedule
   * @param {object} options
   * @param {string} options.type - 'overdue', 'stopped', or 'all'
   * @param {string} [options.scheduledDateTime] - ISO string for when to schedule (optional)
   * @param {string} [options.templateId] - Template ID to override for this step (optional)
   */
  async rescheduleCampaignStep(campaignId, step, options = {}) {
    const { type = 'all', scheduledDateTime = null, templateId = null } = options;
    try {
      // Get campaign activation config
      const { data: activation, error: activationError } = await supabase
        .from('email_automation_campaign_activation')
        .select('*')
        .eq('campaign_id', campaignId)
        .eq('is_active', true)
        .single();

      if (activationError || !activation) {
        logger.error('Campaign activation not found:', activationError);
        return { rescheduled: 0, error: 'Campaign activation not found or inactive' };
      }

      const timeWindow = activation.sending_time_window;
      let sequencesToReschedule = [];

      // Get overdue sequences at this step
      if (type === 'overdue' || type === 'all') {
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
        const { data: overdueSeqs, error: oErr } = await supabase
          .from('email_automation_sequences')
          .select('*')
          .eq('campaign_id', campaignId)
          .eq('current_step', step)
          .eq('status', 'active')
          .not('next_scheduled_at', 'is', null)
          .lt('next_scheduled_at', oneHourAgo);

        if (!oErr && overdueSeqs) {
          sequencesToReschedule.push(...overdueSeqs.map(s => ({ ...s, reschedule_type: 'overdue' })));
        }
      }

      // Get auto-reply-stopped sequences at this step
      if (type === 'stopped' || type === 'all') {
        const { data: stoppedSeqs, error: sErr } = await supabase
          .from('email_automation_sequences')
          .select('*')
          .eq('campaign_id', campaignId)
          .eq('current_step', step)
          .in('status', ['stopped', 'replied']);

        if (!sErr && stoppedSeqs) {
          sequencesToReschedule.push(...stoppedSeqs.map(s => ({ ...s, reschedule_type: 'stopped' })));
        }
      }

      if (sequencesToReschedule.length === 0) {
        return { rescheduled: 0, step, type, campaign_id: campaignId };
      }

      // If template override is specified, update the campaign config for this step
      if (templateId) {
        try {
          const currentConfig = activation.sequence_config || {};
          const steps = currentConfig.steps || [];
          const updatedSteps = steps.map(s => {
            if (s.step === step) {
              return { ...s, template_id: templateId };
            }
            return s;
          });

          await supabase
            .from('email_automation_campaign_activation')
            .update({
              sequence_config: { ...currentConfig, steps: updatedSteps },
              updated_at: new Date().toISOString()
            })
            .eq('id', activation.id);

          logger.log(`✅ Updated template for step ${step} to ${templateId}`);
        } catch (templateError) {
          logger.error('Error updating step template:', templateError);
        }
      }

      // Determine the effective template for this step
      const stepConfig = activation.sequence_config?.steps?.[step - 1];
      const effectiveTemplateId = templateId || stepConfig?.template_id || activation.template_id;

      if (!effectiveTemplateId) {
        logger.error('No template available for step', step);
        return { rescheduled: 0, error: `No template available for step ${step}. Set a campaign template or step template first.` };
      }

      // Get template content
      const { data: template, error: tplError } = await supabase
        .from('email_automation_templates')
        .select('*')
        .eq('id', effectiveTemplateId)
        .single();

      if (tplError || !template) {
        logger.error('Template not found:', effectiveTemplateId);
        return { rescheduled: 0, error: 'Template not found' };
      }

      // Get time window settings for randomization (stagger between emails)
      const minDelayMinutes = timeWindow?.min_delay_minutes || 1;
      const maxDelayMinutes = timeWindow?.max_delay_minutes || 10;
      const dailyLimitConfig = activation.daily_limit || {};
      const maxPerDay = (dailyLimitConfig.enabled !== false) ? (dailyLimitConfig.max_per_day || 50) : 999;

      // Calculate base time
      let baseTime;
      if (scheduledDateTime) {
        baseTime = new Date(scheduledDateTime);
      } else {
        baseTime = new Date();
      }

      // Check existing sends + pending for the base date to respect daily limit
      const baseDateStr = baseTime.toISOString().split('T')[0];
      const { count: existingSendsForDay } = await supabase
        .from('email_automation_executions')
        .select('*', { count: 'exact', head: true })
        .in('status', ['sent', 'pending'])
        .gte('scheduled_send_time', `${baseDateStr}T00:00:00`)
        .lte('scheduled_send_time', `${baseDateStr}T23:59:59`);

      let currentDayCount = existingSendsForDay || 0;
      let cumulativeDelayMs = 0;
      let dayOffset = 0;
      let rescheduledCount = 0;

      logger.log(`📋 Rescheduling ${sequencesToReschedule.length} sequences for step ${step}, base time: ${baseTime.toISOString()}`);
      logger.log(`   ⏰ Stagger: ${minDelayMinutes}-${maxDelayMinutes} min between emails, daily limit: ${maxPerDay}, existing today: ${currentDayCount}`);

      for (const seq of sequencesToReschedule) {
        try {
          // Check daily limit - if exceeded, move to next day
          if (currentDayCount >= maxPerDay) {
            dayOffset++;
            currentDayCount = 0;
            cumulativeDelayMs = 0;
            logger.log(`   📅 Daily limit (${maxPerDay}) reached, moving to day +${dayOffset}`);
          }

          // Check for existing pending/sent execution for this sequence+step to prevent duplicates
          const { count: existingExecution } = await supabase
            .from('email_automation_executions')
            .select('*', { count: 'exact', head: true })
            .eq('sequence_id', seq.id)
            .eq('sequence_step', step)
            .in('status', ['pending', 'sent']);

          if (existingExecution > 0) {
            logger.log(`   ⏭️ Skipping ${seq.lead_email} — already has pending/sent execution for step ${step}`);
            continue;
          }

          // Add random delay between min and max for staggering
          const randomDelayMs = (minDelayMinutes + Math.random() * (maxDelayMinutes - minDelayMinutes)) * 60 * 1000;
          cumulativeDelayMs += randomDelayMs;

          const scheduledSendTime = new Date(baseTime.getTime() + cumulativeDelayMs + (dayOffset * 24 * 60 * 60 * 1000));

          // Get lead data for template variable substitution
          const { data: lead } = await supabase
            .from('leads')
            .select('*')
            .eq('email', seq.lead_email)
            .single();

          // Process template variables (same logic as orchestrator)
          let emailSubject = template.follow_up_subject || template.subject_template || '';
          let emailContent = template.follow_up_content || template.content_template || '';

          if (lead) {
            emailSubject = emailSubject
              .replace(/\{\{first_name\}\}/g, lead.first_name || 'there')
              .replace(/\{\{company\}\}/g, lead.company || 'your company')
              .replace(/\{\{email\}\}/g, lead.email || '');
            emailContent = emailContent
              .replace(/\{\{first_name\}\}/g, lead.first_name || 'there')
              .replace(/\{\{company\}\}/g, lead.company || 'your company')
              .replace(/\{\{email\}\}/g, lead.email || '');
          }

          // Create execution record directly (no need to wait for orchestrator cron)
          const { error: execError } = await supabase
            .from('email_automation_executions')
            .insert({
              sequence_id: seq.id,
              lead_email: seq.lead_email,
              template_id: effectiveTemplateId,
              sequence_step: step,
              status: 'pending',
              scheduled_send_time: scheduledSendTime.toISOString(),
              scheduled_at: scheduledSendTime.toISOString(),
              email_subject: emailSubject,
              email_content: emailContent,
              original_inbox_email: seq.original_inbox_email,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });

          if (execError) {
            logger.error(`Error creating execution for ${seq.lead_email}:`, execError);
            continue;
          }

          // Update sequence: set to active, set next_scheduled_at for UI visibility
          // Orchestrator has duplicate execution check so it won't create a second one
          const { error: updateError } = await supabase
            .from('email_automation_sequences')
            .update({
              status: 'active',
              next_scheduled_at: scheduledSendTime.toISOString(),
              updated_at: new Date().toISOString()
            })
            .eq('id', seq.id);

          if (!updateError) {
            rescheduledCount++;
            currentDayCount++;
            logger.log(`   ✅ ${seq.lead_email} → ${scheduledSendTime.toISOString()}`);
          } else {
            logger.error(`Error updating sequence ${seq.id}:`, updateError);
          }
        } catch (seqError) {
          logger.error(`Error processing sequence ${seq.id}:`, seqError);
        }
      }

      logger.log(`✅ Rescheduled ${rescheduledCount}/${sequencesToReschedule.length} sequences for campaign ${campaignId} step ${step} (type: ${type})`);
      return {
        rescheduled: rescheduledCount,
        total: sequencesToReschedule.length,
        step,
        type,
        campaign_id: campaignId
      };
    } catch (error) {
      logger.error('Error in rescheduleCampaignStep:', error);
      return { rescheduled: 0, error: error.message };
    }
  }

  /**
   * Get execution statistics: total, sent, failed, pending
   * Used by the dashboard to show all execution counts + failed count
   */
  async getExecutionStats() {
    try {
      const { count: totalCount } = await supabase
        .from('email_automation_executions')
        .select('*', { count: 'exact', head: true });

      const { count: sentCount } = await supabase
        .from('email_automation_executions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'sent');

      const { count: failedCount } = await supabase
        .from('email_automation_executions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'failed');

      const { count: pendingCount } = await supabase
        .from('email_automation_executions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      return {
        total: totalCount || 0,
        sent: sentCount || 0,
        failed: failedCount || 0,
        pending: pendingCount || 0
      };
    } catch (error) {
      logger.error('Error in getExecutionStats:', error);
      return { total: 0, sent: 0, failed: 0, pending: 0 };
    }
  }
}

// Create singleton instance
const advancedEmailAutomationService = new AdvancedEmailAutomationService();

export default advancedEmailAutomationService;